/**
 * @author       justin.jeong@zumper.com (Justin Jeong)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  error filters for thunks and the like
 */

const xzTokenReg = /xz_token/i
const csrfTokenReg = /csrf/i

export interface ErrorData {
  reason?: string
  status?: number
}

export interface ZError {
  data?: ErrorData
  message?: string
  name?: string
}

const isErrorData = (data: any): data is ErrorData =>
  typeof data === 'object' &&
  !!data &&
  ['undefined', 'string'].includes(typeof data.reason) &&
  ['undefined', 'number'].includes(typeof data.status)

const isZError = (error: any): error is ZError =>
  typeof error === 'object' &&
  !!error &&
  (error.data ? isErrorData(error.data) : true) &&
  ['undefined', 'string'].includes(typeof error.message) &&
  ['undefined', 'string'].includes(typeof error.name)

export const isCommonAuthError = (error: unknown): error is ZError =>
  isZError(error) && error.data?.reason
    ? xzTokenReg.test(error.data.reason) || csrfTokenReg.test(error.data.reason)
    : false

export const isCookieError = (error: unknown): error is ZError =>
  isZError(error) &&
  !!(
    error.data?.reason?.includes?.('cookie not set') &&
    !window?.navigator?.cookieEnabled
  )

export const isFailedToFetchError = (error: unknown): error is ZError =>
  isZError(error) && !!error.message?.includes?.('Failed to fetch')

// This is for failing chunks for the price chart in the rent trends loadable section.
export const isPriceChartFailedChunkError = (error: unknown): error is ZError =>
  isZError(error) &&
  !!error.message?.includes?.(
    'Property description must be an object: undefined'
  )

export const is404Error = (error: unknown): error is ZError =>
  isZError(error) && !!(error.data?.status === 404)

export const isWebGLError = (error: unknown): error is ZError =>
  isZError(error) &&
  (!!error.message?.includes?.('Failed to initialize WebGL') ||
    !!error.message?.includes?.("Failed to execute 'shaderSource'"))

export const isInvalidEmailDomainError = (data: any): data is ErrorData => {
  return data.reason === 'invalid email domain' && data.status >= 400
}

export const isErrorish = (error: unknown): error is { message: string } => {
  return (
    !!error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  )
}
